
import { Card, CardContent } from "@/components/ui/card";
import { ProfileAvatar } from "@/components/ui/ProfileAvatar";
import { MessageCircle, ExternalLink } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "@/lib/router-compat";
import type { Database } from "@/integrations/supabase/types";
import { T } from "@/i18n/T";

type CommentActivity = Database['public']['Tables']['article_comments']['Row'] & {
  articles: Database['public']['Tables']['articles']['Row'];
  user_profiles: Database['public']['Tables']['user_profiles']['Row'];
};

const CommentActivityCard = ({ comment }: { comment: CommentActivity }) => {
  const timeAgo = formatDistanceToNow(new Date(comment.created_at), { addSuffix: true });

  return (
    <Card className="hover:bg-muted/30 transition-colors">
      <CardContent className="p-4">
        <div className="flex gap-3">
          <Link to={`/profile/${comment.user_id}`}>
            <ProfileAvatar
              userId={comment.user_id}
              avatarUrl={comment.user_profiles?.avatar_url}
              name={comment.user_profiles?.full_name}
              background={comment.user_profiles?.avatar_background}
              size="md"
            />
          </Link>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Link to={`/profile/${comment.user_id}`} className="font-semibold text-sm hover:underline flex items-center gap-1">
                {comment.user_profiles?.full_name || "Unknown User"}
              </Link>
              <Link to={`/profile/${comment.user_id}`} className="text-muted-foreground text-sm hover:underline">
                @{comment.user_profiles?.username || "user"}
              </Link>
              <span className="text-muted-foreground text-sm">·</span>
              <span className="text-muted-foreground text-sm">{timeAgo}</span>
            </div>
            
            <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
              <MessageCircle className="h-3.5 w-3.5" />
              <span><T k="auto.commented-on-article-bf0a" /></span>
            </div>
            
            <div className="text-sm leading-relaxed mb-3 bg-muted/50 p-3 rounded-lg">
              {comment.content}
            </div>
            
            {comment.articles && (
              <Link to={`/article/${(comment.articles as any).slug || comment.articles.id}`}>
                <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
                  <CardContent className="p-3">
                    <div className="flex gap-3">
                      {comment.articles.image_url && (
                        <img 
                          src={comment.articles.image_url} 
                          alt={comment.articles.title}
                          className="w-16 h-16 object-cover rounded-md shrink-0"
                        loading="lazy" decoding="async" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm line-clamp-2 mb-1">
                          {comment.articles.title}
                        </h4>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {comment.articles.excerpt}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-blue-600 font-medium">
                            {comment.articles.source_name}
                          </span>
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CommentActivityCard;
